import { useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '@alltrails/shared/react-intl';
import logTrailDetailsGpxDownloadButtonTapped from '@alltrails/analytics/events/logTrailDetailsGpxDownloadButtonTapped';
import {
  logTrailDetailsOverflowItemSelected,
  logTrailDetailsPrintClicked,
  logTrailDetailsShareClicked,
  logTrailDownloadButtonTapped,
  TrailDetailsOverflowItem
} from 'utils/trail_analytics_helpers';
import useUser from 'hooks/useUser';
import { Trail } from 'types/Trails';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import SignupModal from 'components/SignupModal';
import useToggle from '@alltrails/shared/hooks/useToggle';
import PlusBadge from '@alltrails/shared/denali/components/PlusBadge';
import ShareObject from 'types/ShareObject';
import { renderShareModal } from 'react_components/main_action_bar/share_modal';
import { wrapUrlSafe } from 'utils/language_support_util';
import { wrapEventHandler } from 'utils/handlers';
import { copyVerifiedRoute } from 'utils/requests/at_map_requests';
import { modalRoadblock } from '@alltrails/shared/utils/modalFunnelUtils';
import { ServerCommunicationUtil } from 'utils/server_communication_util';
import logError from 'utils/logError';
import PortalModal from 'components/shared/PortalModal';
import ProgressDialog from '../../shared/ProgressDialog';
import DownloadRouteModal from '../../DownloadRouteModal';
import * as styles from './TrailMoreAlternateDropDownList.module.scss';
import CarouselDisplayTrigger from '@alltrails/analytics/enums/CarouselDisplayTrigger';

type Props = {
  exploreFlag: boolean;
  isMobileWidth: boolean;
  mobileBrowser: boolean;
  pending: boolean;
  trail: Trail;
  printUrl: string;
};

const TrailMoreAlternateDropDownList = ({ exploreFlag, isMobileWidth, mobileBrowser, pending, printUrl, trail }: Props) => {
  const [isOpen, toggle] = useToggle();
  const user = useUser();
  const languageRegionCode = useLanguageRegionCode();
  const [showShareModal, setShowShareModal] = useState(false);
  const [progressDialogState, setProgressDialogState] = useState();
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadObject, setDownloadObject] = useState<ShareObject>();

  const closeShareModal = () => {
    setShowShareModal(false);
    (document.querySelector('.hide-for-fullscreen-modal') as HTMLElement).style.display = '';
  };

  const handleShareClick = () => {
    logTrailDetailsShareClicked(trail.ID);

    setShowShareModal(true);

    const hideForFullScreenModal = document.querySelector('.hide-for-fullscreen-modal') as HTMLElement;

    if (hideForFullScreenModal) {
      hideForFullScreenModal.style.display = 'none';
    }

    return true;
  };

  const handleDeleteTrailClick = () => {
    if (!window.confirm('Are you sure you want to delete this trail?')) {
      return;
    }

    const url = `/api/alltrails/trails/${trail.trail_id}`;

    ServerCommunicationUtil.deleteApiEndpoint(
      url,
      {},
      () => {
        window.location.href = wrapUrlSafe('/explore', languageRegionCode);
      },
      (xhr: any, status: any, error: any) => {
        logError(error);
      }
    );
  };

  const handleEditClick = () => {
    const returnToUrl = window.location.pathname + window.location.search;

    logTrailDetailsOverflowItemSelected(trail.ID, TrailDetailsOverflowItem.SuggestTrailEdit);
    if (!user) {
      modalRoadblock('signup', 'trail-more-edit', returnToUrl, languageRegionCode);
      return false;
    }

    return true;
  };

  const updateProgressDialog = (state: any) => {
    setProgressDialogState(state);
  };

  const renderProgressDialog = () => {
    if (!progressDialogState) {
      return null;
    }

    return (
      <PortalModal isOpen={progressDialogState}>
        <ProgressDialog isMobileWidth={isMobileWidth} variant={progressDialogState} updateProgressDialog={updateProgressDialog} />
      </PortalModal>
    );
  };

  const handleMapSaveError = (err: any) => {
    updateProgressDialog('error');
    logError(err);
    return err;
  };

  const redirectToMapPage = (map: any) => {
    window.top.location.href = wrapUrlSafe(`/explore/map/${map.slug}?save`, languageRegionCode);
  };

  const blockUserNewMapCreation = () => {
    const returnToUrl = window.location.pathname + window.location.search;
    modalRoadblock('signup', 'explore-map-new', returnToUrl, languageRegionCode);
  };

  const saveMapAsClone = () => {
    logTrailDetailsOverflowItemSelected(trail.ID, TrailDetailsOverflowItem.CustomizeMap);

    // Block user with signup if not signed-in
    if (!user) {
      blockUserNewMapCreation();
      return;
    }

    const { trail_id, name } = trail;
    updateProgressDialog('saving');
    copyVerifiedRoute(trail_id, name).then(redirectToMapPage).catch(handleMapSaveError);
  };

  const toggleDownloadModal = () => {
    setShowDownloadModal(!showDownloadModal);
  };

  const openDownloadModal = () => {
    logTrailDownloadButtonTapped(trail.trail_id);

    const publishObject = {
      id: trail.trail_id,
      name: trail.name,
      slug: trail.slug,
      type: 'trail' as const,
      primaryKey: 'trail_id' as const
    };

    if (!user) {
      logTrailDetailsGpxDownloadButtonTapped({ trail_id: trail.trail_id });
      const returnToUrl = window.location.pathname + window.location.search;
      modalRoadblock('signup', 'trail-more-gpx', returnToUrl, languageRegionCode);
    }

    setShowDownloadModal(true);
    setDownloadObject(publishObject);
  };

  // Explore or Mobile
  let printUrlItem = null;
  if (exploreFlag || isMobileWidth) {
    if (!mobileBrowser) {
      printUrlItem = (
        <div className={styles.dropdownContent}>
          <a
            data-testid="trail_more_alternate_print_pdf"
            className={styles.gpxLabel}
            // 2022-06-16 per Sam _blank was removed so we redirect the user to /pro in the same window
            href={printUrl}
            onClick={() => logTrailDetailsPrintClicked(trail.ID)}
          >
            <span className={styles.printItem}>
              <PlusBadge size="sm" />
            </span>
            <FormattedMessage defaultMessage="Print/PDF map" />
          </a>
        </div>
      );
    }
  }

  // Requires Signed-in User
  let suggestEditItem = null;
  if (user) {
    const suggestUrl = wrapUrlSafe(`/${trail.slug}/edit?ref=trail-more-edit`, languageRegionCode);
    // suggest edit display: none for screen sizes under 767 (unless being displayed in trail-explore left panel)
    suggestEditItem = (
      <div className={classNames(styles.dropdownContent, 'suggest', !exploreFlag && 'trail-details')}>
        <a href={suggestUrl} target="_blank" onClick={handleEditClick} data-testid="trail_more_alternate_suggest_edit" rel="noreferrer">
          <FormattedMessage defaultMessage="Suggest edit" />
        </a>
      </div>
    );
  }

  return (
    <>
      <SignupModal
        trigger={CarouselDisplayTrigger.ShareTrail}
        isOpen={isOpen}
        onRequestClose={() => {
          toggle();
        }}
        onSuccess={() => {
          toggle();
          handleShareClick();
        }}
      />
      <div className={styles.dropdown}>
        {suggestEditItem}
        {user?.admin && (
          <div className={styles.dropdownContent} onClick={handleDeleteTrailClick}>
            <a data-testid="trail_more_alternate_delete_trail">Delete Trail</a>
          </div>
        )}
        {(exploreFlag || isMobileWidth) && (
          <div className={styles.dropdownContent}>
            <button
              className={styles.button}
              data-testid="trail_more_alternate_share"
              onClick={() => {
                if (!user) {
                  toggle();
                } else {
                  handleShareClick();
                }
              }}
              type="button"
            >
              <FormattedMessage defaultMessage="Share" />
            </button>
          </div>
        )}
        {!pending && (
          <div className={styles.dropdownContent}>
            <a data-testid="trail_more_alternate_download_route" onClick={openDownloadModal}>
              <FormattedMessage defaultMessage="Download route" />
            </a>
          </div>
        )}
        <div className={styles.dropdownContent}>
          <a data-testid="trail_more_alternate_customize_map" key="customizeMap" onClick={wrapEventHandler(saveMapAsClone)}>
            <FormattedMessage defaultMessage="Customize map" />
          </a>
        </div>
        {printUrlItem}
      </div>
      {renderShareModal(
        showShareModal,
        closeShareModal,
        {
          id: trail.trail_id,
          name: trail.name,
          slug: trail.slug,
          type: 'trail' as const,
          primaryKey: 'trail_id' as const
        },
        user
      )}
      {renderProgressDialog()}
      {user && showDownloadModal && <DownloadRouteModal toggleDownloadModal={toggleDownloadModal} trailSlug={downloadObject?.slug} />}
    </>
  );
};

export default TrailMoreAlternateDropDownList;
